import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { 
  ArrowRight, 
  Brain, 
  Target, 
  Users, 
  TrendingUp, 
  BarChart3,
  Calendar,
  AlertTriangle,
  Lightbulb,
  Layers,
  Building2,
  Play,
  BookOpen
} from 'lucide-react';

const SHAYAMAL_SPEAKING = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/lvlgfpli_IMG_9913.jpg";
const SHAYAMAL_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/vxkjfdze_new%20version.jpg";

// Dashboard mockup images - User's actual platform UI
const DASHBOARD_OVERVIEW = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/x498t6nn_demo.png";
const DASHBOARD_TEAM = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/7q4b2nhr_demo%202.png";
const DASHBOARD_ASSESSMENT = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/q9bwee1e_demo%203.png";
const DASHBOARD_COURSES = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/nrhtbjat_demo%204.png";

export default function AILeadershipPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  
  const problemRef = useRef(null);
  const solutionRef = useRef(null);
  const platformRef = useRef(null);
  
  const isProblemInView = useInView(problemRef, { once: true, margin: "-100px" });
  const isSolutionInView = useInView(solutionRef, { once: true, margin: "-100px" });
  const isPlatformInView = useInView(platformRef, { once: true, margin: "-100px" });

  const thinkingLayers = [
    {
      step: "01",
      title: "Critical Thinking",
      description: "Signal vs Noise. Being able to ask the right question in a world drowning in information.",
      icon: Target
    },
    {
      step: "02", 
      title: "Systems Thinking",
      description: "Take that answer and build it into a repeatable, scalable process.",
      icon: Layers
    },
    {
      step: "03",
      title: "Lateral Thinking", 
      description: "Extrapolate that process across different industries and contexts.",
      icon: Lightbulb
    }
  ];

  const platformTabs = [
    { id: 'dashboard', label: 'Your Dashboard', image: DASHBOARD_OVERVIEW },
    { id: 'team', label: 'Team Overview', image: DASHBOARD_TEAM },
    { id: 'assessment', label: 'Assessments', image: DASHBOARD_ASSESSMENT },
    { id: 'courses', label: 'Customised Courses', image: DASHBOARD_COURSES },
  ];

  const workingOptions = [
    {
      title: "Foundation Workshop",
      duration: "Half-day or Full-day",
      description: "Set the foundation for how to think, learn, and lead in the AI era. Perfect for leadership teams ready to shift their mindset.",
      ideal: "C-Suite & Senior Leadership",
      icon: Users
    },
    {
      title: "Signal Platform",
      duration: "Per Seat / Per Year",
      description: "Full platform access for your team — assessments, bite-sized courses, leader dashboards, progress analytics, and quarterly live coaching sessions.",
      ideal: "Teams of 20+",
      icon: TrendingUp,
      featured: true
    },
    {
      title: "Enterprise",
      duration: "Custom Pricing",
      description: "Organization-wide deployment with department-level analytics, executive dashboards, dedicated support, and custom content modules.",
      ideal: "Organizations 500+",
      icon: Building2
    }
  ];

  return (
    <main data-testid="ai-leadership-page" className="bg-[#F4F1EC]">
      {/* Hero Section with VSL Placeholder */}
      <section className="relative min-h-screen flex items-center bg-[#202b37] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#e17c18] rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#e8dcc7] rounded-full blur-[150px]" />
        </div>
        
        <div className="container-peak relative z-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left px-4"
            >
              <span className="inline-block px-4 py-2 bg-[#e17c18]/20 text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">
                For Organizations Ready to Lead
              </span>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.1] mb-6 text-[#e8dcc7]">
                The Real Leverage in the{' '}
                <em className="not-italic text-[#e17c18]">AI World</em>
              </h1>
              
              <p className="text-[#e8dcc7]/80 text-base lg:text-lg mb-6 max-w-xl">
                Equip your entire organization — leaders and their teams — with the right tools. And the right tools aren't just AI tools.
              </p>
              
              <p className="text-[#e8dcc7] text-lg lg:text-xl mb-10 max-w-xl font-['Cormorant_Garamond']">
                It's the first-principle human skills that will make them capable of thriving through all the changes in the next decade.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="https://calendar.app.google/evsn9LsubajagFF3A"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-3"
                >
                  Talk to Us
                  <ArrowRight size={16} />
                </a>
                <a
                  href="#platform"
                  className="inline-flex items-center justify-center gap-3 font-['Syne'] text-xs uppercase tracking-[0.12em] text-[#e8dcc7] border border-[#e8dcc7]/30 px-8 py-4 hover:bg-[#e8dcc7]/10 transition-all"
                >
                  See The Platform
                </a>
              </div>
            </motion.div>

            {/* VSL Placeholder */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-video bg-[#1a2330] border border-[#e8dcc7]/10 flex items-center justify-center relative overflow-hidden group cursor-pointer">
                <div className="absolute inset-0 bg-gradient-to-br from-[#e17c18]/5 to-transparent" />
                <div className="text-center z-10">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#e17c18] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </div>
                  <p className="text-[#e8dcc7]/60 text-sm font-['Syne'] uppercase tracking-wider">Watch the 3-min overview</p>
                </div>
                <div className="absolute bottom-4 right-4 text-[#e8dcc7]/30 text-xs">Coming Soon</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section ref={problemRef} className="py-20 lg:py-28 bg-[#F4F1EC]">
        <div className="container-peak">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isProblemInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-16">
              <span className="section-tag block mb-4">The Reality</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#202b37]">
                Organizations that fail to adapt{' '}
                <em className="not-italic text-[#e17c18]">don't survive</em>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-8 border border-[#E6E1DA]">
                <AlertTriangle className="w-8 h-8 text-[#e17c18] mb-4" />
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#202b37] mb-3">The Pace Problem</h3>
                <p className="text-[#5A6B7A] text-sm leading-relaxed">
                  Technology that seemed cutting-edge becomes irrelevant in weeks. The pace of change is outpacing everyone. More companies will fail in the next decade than ever before.
                </p>
              </div>
              
              <div className="bg-white p-8 border border-[#E6E1DA]">
                <Brain className="w-8 h-8 text-[#e17c18] mb-4" />
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#202b37] mb-3">The Real Threat</h3>
                <p className="text-[#5A6B7A] text-sm leading-relaxed">
                  Knowledge is now a commodity. Experience can be replicated instantly. If both are commodities, what's your actual competitive advantage?
                </p>
              </div>
            </div>

            <div className="bg-[#202b37] p-8 lg:p-12 text-center">
              <p className="text-[#e8dcc7] text-lg lg:text-xl font-['Cormorant_Garamond'] leading-relaxed mb-6">
                "What a top consulting firm does for hundreds of thousands — AI can do in minutes."
              </p>
              <p className="text-[#e8dcc7]/70 text-sm">
                It's not a skill problem. It's not a knowledge problem.<br />
                <span className="text-[#e17c18]">It's a thinking and adaptability problem.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Two Types of Leaders */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-peak">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <span className="section-tag block mb-4">The Leaders in the Room</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#202b37]">
                Which one are <em className="not-italic text-[#e17c18]">you</em>?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-8 border-2 border-[#e17c18]/20 bg-[#F4F1EC]">
                <div className="text-[#e17c18] font-['Syne'] text-xs uppercase tracking-wider mb-4">Type 1</div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#202b37] mb-4">The Excited Underuser</h3>
                <p className="text-[#5A6B7A] text-sm leading-relaxed">
                  Excited about AI — but using 0.1% of its potential. Like owning a supercomputer and only checking email.
                </p>
              </div>
              
              <div className="p-8 border-2 border-[#202b37]/20 bg-[#F4F1EC]">
                <div className="text-[#202b37] font-['Syne'] text-xs uppercase tracking-wider mb-4">Type 2</div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#202b37] mb-4">The Fearful Avoider</h3>
                <p className="text-[#5A6B7A] text-sm leading-relaxed">
                  Absolutely fearful — staying "busy" as a smokescreen. Busyness masks the reality that the world is moving faster than they can keep up.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-[#5A6B7A] text-base lg:text-lg max-w-2xl mx-auto">
                When leaders say "we need to get our people up to speed" — they're often masking the fact that <span className="text-[#202b37] font-medium">they</span> aren't up to speed. They become the bottleneck.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Reimagined - Triangle Visual */}
      <section className="py-20 lg:py-28 bg-[#202b37] overflow-hidden">
        <div className="container-peak">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#e17c18]/20 text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">
              A New Approach
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#e8dcc7] mb-6">
              When the World Changes,{' '}
              <em className="not-italic text-[#e17c18]">Learning Must Evolve</em>
            </h2>
            <p className="text-[#e8dcc7]/70 text-base lg:text-lg max-w-2xl mx-auto">
              Traditional training doesn't work anymore. We've built something different — a system where coaching, self-paced learning, and measurable progress work together.
            </p>
          </div>

          {/* Three Pillars Visual */}
          <div className="max-w-5xl mx-auto">
            {/* Triangle Layout */}
            <div className="relative py-8 lg:py-12">
              {/* SVG Triangle Background */}
              <svg viewBox="0 0 600 400" className="w-full max-w-xl lg:max-w-3xl mx-auto absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30" preserveAspectRatio="xMidYMid meet">
                <path 
                  d="M300 60 L540 340 L60 340 Z" 
                  fill="none" 
                  stroke="#e17c18" 
                  strokeWidth="1"
                />
                <line x1="300" y1="60" x2="300" y2="200" stroke="#e17c18" strokeWidth="0.5" strokeDasharray="4,4" />
                <line x1="540" y1="340" x2="300" y2="200" stroke="#e17c18" strokeWidth="0.5" strokeDasharray="4,4" />
                <line x1="60" y1="340" x2="300" y2="200" stroke="#e17c18" strokeWidth="0.5" strokeDasharray="4,4" />
              </svg>

              {/* Center Circle */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full border-2 border-[#e17c18] bg-[#202b37] flex flex-col items-center justify-center">
                  <span className="text-[#e8dcc7] text-[10px] lg:text-xs font-['Syne'] uppercase tracking-wider">Your</span>
                  <span className="text-[#e17c18] text-xs lg:text-sm font-['Syne'] uppercase tracking-wider">Team</span>
                </div>
              </div>

              {/* Top Card - Coaching */}
              <div className="flex justify-center mb-6 lg:mb-8">
                <div className="bg-[#1a2330] border border-[#e17c18]/30 px-5 py-4 lg:px-8 lg:py-6 text-center max-w-[180px] lg:max-w-[220px]">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 mx-auto mb-2 lg:mb-3 bg-[#e17c18]/20 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 lg:w-6 lg:h-6 text-[#e17c18]" />
                  </div>
                  <h4 className="text-[#e8dcc7] font-['Syne'] text-[10px] lg:text-xs uppercase tracking-wider mb-1 lg:mb-2">Live Coaching</h4>
                  <p className="text-[#e8dcc7]/60 text-xs lg:text-sm">Learn from a world-class performance coach</p>
                </div>
              </div>

              {/* Spacer for triangle height */}
              <div className="h-28 lg:h-40"></div>

              {/* Bottom Cards */}
              <div className="flex justify-between items-start px-2 lg:px-8">
                <div className="bg-[#1a2330] border border-[#e17c18]/30 px-4 py-4 lg:px-8 lg:py-6 text-center max-w-[150px] lg:max-w-[220px]">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 mx-auto mb-2 lg:mb-3 bg-[#e17c18]/20 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 lg:w-6 lg:h-6 text-[#e17c18]" />
                  </div>
                  <h4 className="text-[#e8dcc7] font-['Syne'] text-[10px] lg:text-xs uppercase tracking-wider mb-1 lg:mb-2">Progress Tracking</h4>
                  <p className="text-[#e8dcc7]/60 text-xs lg:text-sm">Leaders see team growth. Individuals track their own.</p>
                </div>

                <div className="bg-[#1a2330] border border-[#e17c18]/30 px-4 py-4 lg:px-8 lg:py-6 text-center max-w-[150px] lg:max-w-[220px]">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 mx-auto mb-2 lg:mb-3 bg-[#e17c18]/20 rounded-full flex items-center justify-center">
                    <BookOpen className="w-5 h-5 lg:w-6 lg:h-6 text-[#e17c18]" />
                  </div>
                  <h4 className="text-[#e8dcc7] font-['Syne'] text-[10px] lg:text-xs uppercase tracking-wider mb-1 lg:mb-2">Self-Paced Platform</h4>
                  <p className="text-[#e8dcc7]/60 text-xs lg:text-sm">Personalized courses each member completes at their pace</p>
                </div>
              </div>
            </div>

            {/* Bottom text */}
            <div className="text-center mt-8 lg:mt-16">
              <p className="text-[#e8dcc7]/80 text-base lg:text-lg max-w-xl mx-auto font-['Cormorant_Garamond'] italic">
                "Three pillars. One integrated system. Real, measurable transformation for your entire organization."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution - Signal vs Noise */}
      <section ref={solutionRef} className="py-20 lg:py-28 bg-[#F4F1EC]">
        <div className="container-peak">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isSolutionInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="text-center mb-16">
              <span className="section-tag block mb-4">Our Framework</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#202b37] mb-6">
                The Three Thinking Layers
              </h2>
              <p className="text-[#5A6B7A] text-base lg:text-lg max-w-2xl mx-auto">
                The fundamental skills that grow and adapt as the world changes — the foundation everything else is built on.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {thinkingLayers.map((layer, index) => (
                <motion.div
                  key={layer.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isSolutionInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-white p-8 border border-[#E6E1DA] relative"
                >
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#202b37] flex items-center justify-center">
                    <span className="text-[#e17c18] font-['Syne'] text-sm">{layer.step}</span>
                  </div>
                  <div className="pt-4">
                    <layer.icon className="w-8 h-8 text-[#e17c18] mb-4" />
                    <h3 className="font-['Cormorant_Garamond'] text-xl text-[#202b37] mb-3">{layer.title}</h3>
                    <p className="text-[#5A6B7A] text-sm leading-relaxed">{layer.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works - Simplified */}
      <section className="py-20 lg:py-28 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="text-center mb-16">
            <span className="section-tag block mb-4">The Journey</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#202b37]">
              How It Works
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-[#E6E1DA]" />

              {/* Step 1 */}
              <div className="grid md:grid-cols-2 gap-8 mb-16 relative">
                <div className="md:text-right md:pr-12">
                  <div className="inline-flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 bg-[#e17c18] flex items-center justify-center text-white font-['Syne'] text-sm">01</span>
                    <span className="md:hidden font-['Syne'] text-xs uppercase tracking-wider text-[#e17c18]">Foundation</span>
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#202b37] mb-3">Leadership Workshop</h3>
                  <p className="text-[#5A6B7A] text-sm">Half-day or full-day session with your leadership team. Set the foundation, align on the vision, and prepare for transformation.</p>
                </div>
                <div className="hidden md:flex items-center">
                  <img src={SHAYAMAL_SPEAKING} alt="Workshop" className="w-full max-w-sm rounded shadow-lg" />
                </div>
              </div>

              {/* Step 2 */}
              <div className="grid md:grid-cols-2 gap-8 mb-16 relative">
                <div className="md:order-2 md:pl-12">
                  <div className="inline-flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 bg-[#e17c18] flex items-center justify-center text-white font-['Syne'] text-sm">02</span>
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#202b37] mb-3">Team Onboarding</h3>
                  <p className="text-[#5A6B7A] text-sm">Every team member gets platform access. They complete initial assessments, and the system creates their personalized learning journey based on their gaps.</p>
                </div>
                <div className="md:order-1 hidden md:flex items-center justify-end">
                  <img src={DASHBOARD_OVERVIEW} alt="Platform" className="w-full max-w-sm rounded shadow-lg" />
                </div>
              </div>

              {/* Step 3 */}
              <div className="grid md:grid-cols-2 gap-8 relative">
                <div className="md:text-right md:pr-12">
                  <div className="inline-flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 bg-[#e17c18] flex items-center justify-center text-white font-['Syne'] text-sm">03</span>
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#202b37] mb-3">Ongoing Growth</h3>
                  <p className="text-[#5A6B7A] text-sm">Team members learn at their own pace. Leaders track progress through dashboards. Quarterly live sessions address gaps and celebrate wins.</p>
                </div>
                <div className="hidden md:flex items-center">
                  <div className="bg-[#202b37] p-6 rounded">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src={SHAYAMAL_IMAGE} alt="Shayamal" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-[#e8dcc7] font-medium">Live Coaching</p>
                        <p className="text-[#e8dcc7]/60 text-sm">Based on your data</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 bg-[#e17c18]/30 rounded"><div className="h-full w-3/4 bg-[#e17c18] rounded" /></div>
                      <div className="h-2 bg-[#e17c18]/30 rounded"><div className="h-full w-1/2 bg-[#e17c18] rounded" /></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE PLATFORM - Main Feature Section */}
      <section ref={platformRef} id="platform" className="py-20 lg:py-28 bg-[#202b37]">
        <div className="container-peak">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isPlatformInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-[#e17c18]/20 text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">
                More Than Just Courses
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-light leading-[1.15] text-[#e8dcc7] mb-6">
                Learning That's Actually{' '}
                <em className="not-italic text-[#e17c18]">Measured</em>
              </h2>
              <p className="text-[#e8dcc7]/70 text-base lg:text-lg max-w-2xl mx-auto mb-4">
                Not another library of long courses nobody finishes. Not content that's consumed but never applied. Not training budgets spent with no way to prove ROI.
              </p>
              <p className="text-[#e8dcc7] text-base lg:text-lg max-w-2xl mx-auto">
                This is different. <span className="text-[#e17c18]">Bite-sized learning. Every skill assessed. Every improvement tracked. Leaders see exactly where their team stands.</span>
              </p>
            </div>

            {/* Platform Preview Tabs */}
            <div className="max-w-5xl mx-auto">
              <div className="flex justify-center gap-2 mb-8">
                {platformTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 font-['Syne'] text-xs uppercase tracking-wider transition-all ${
                      activeTab === tab.id
                        ? 'bg-[#e17c18] text-white'
                        : 'bg-[#e8dcc7]/10 text-[#e8dcc7]/70 hover:text-[#e8dcc7]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Dashboard Preview */}
              <div className="relative bg-[#1a2330] p-4 rounded-lg border border-[#e8dcc7]/10">
                <img
                  src={platformTabs.find(t => t.id === activeTab)?.image}
                  alt="Platform Dashboard"
                  className="w-full h-auto rounded shadow-2xl"
                />
              </div>

              {/* Platform Features Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                <div className="text-center p-6">
                  <div className="w-12 h-12 mx-auto mb-4 bg-[#e17c18]/20 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-[#e17c18]" />
                  </div>
                  <h4 className="text-[#e8dcc7] font-medium mb-2">Team Assessments</h4>
                  <p className="text-[#e8dcc7]/60 text-sm">Know exactly where each team member stands</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-12 h-12 mx-auto mb-4 bg-[#e17c18]/20 rounded-full flex items-center justify-center">
                    <Target className="w-6 h-6 text-[#e17c18]" />
                  </div>
                  <h4 className="text-[#e8dcc7] font-medium mb-2">Leader Dashboards</h4>
                  <p className="text-[#e8dcc7]/60 text-sm">Bird's eye view of your team's progress</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-12 h-12 mx-auto mb-4 bg-[#e17c18]/20 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#e17c18]" />
                  </div>
                  <h4 className="text-[#e8dcc7] font-medium mb-2">Org-Wide Analytics</h4>
                  <p className="text-[#e8dcc7]/60 text-sm">Year-end data on team growth across departments</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-12 h-12 mx-auto mb-4 bg-[#e17c18]/20 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[#e17c18]" />
                  </div>
                  <h4 className="text-[#e8dcc7] font-medium mb-2">Team Coaching</h4>
                  <p className="text-[#e8dcc7]/60 text-sm">Quarterly live sessions for your teams</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ways to Work Together */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-peak">
          <div className="text-center mb-16">
            <span className="section-tag block mb-4">Get Started</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#202b37]">
              Choose Your{' '}
              <em className="not-italic text-[#e17c18]">Starting Point</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {workingOptions.map((option, index) => (
              <div 
                key={option.title}
                className={`p-8 border relative ${option.featured ? 'border-[#e17c18] bg-[#F4F1EC]' : 'border-[#E6E1DA] bg-white'}`}
              >
                {option.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#e17c18] px-4 py-1">
                    <span className="text-white text-xs font-['Syne'] uppercase tracking-wider">Recommended</span>
                  </div>
                )}
                <option.icon className="w-10 h-10 text-[#e17c18] mb-4" />
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#202b37] mb-2">{option.title}</h3>
                <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-wider mb-4">{option.duration}</p>
                <p className="text-[#5A6B7A] text-sm leading-relaxed mb-6">{option.description}</p>
                <div className="pt-4 border-t border-[#E6E1DA]">
                  <p className="text-xs text-[#5A6B7A]">Ideal for:</p>
                  <p className="text-[#202b37] text-sm font-medium">{option.ideal}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Peak 9 */}
      <section className="py-20 lg:py-28 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div>
              <span className="section-tag block mb-4">Why Peak 9</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#202b37] mb-6">
                Proven methods.{' '}
                <em className="not-italic text-[#e17c18]">Real results.</em>
              </h2>
              <p className="text-[#5A6B7A] text-base leading-relaxed mb-8">
                We work with CEOs and their direct reports across multinationals in 15+ countries. We've seen what blocks leaders, what they can acknowledge, and what they can't.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white border border-[#E6E1DA] flex items-center justify-center">
                    <span className="text-[#e17c18] font-['Syne'] text-lg font-bold">1K+</span>
                  </div>
                  <p className="text-[#202b37]">Sessions Delivered</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white border border-[#E6E1DA] flex items-center justify-center">
                    <span className="text-[#e17c18] font-['Syne'] text-lg font-bold">50K+</span>
                  </div>
                  <p className="text-[#202b37]">Lives Impacted</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white border border-[#E6E1DA] flex items-center justify-center">
                    <span className="text-[#e17c18] font-['Syne'] text-lg font-bold">18</span>
                  </div>
                  <p className="text-[#202b37]">Countries</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src={SHAYAMAL_IMAGE} 
                alt="Shayamal Vallabhjee"
                className="w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-[#202b37]">
        <div className="container-peak">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#e8dcc7] mb-6">
              When your leaders see AI as their{' '}
              <em className="not-italic text-[#e17c18]">greatest copilot</em>, everything changes.
            </h2>
            <p className="text-[#e8dcc7]/70 text-base lg:text-lg mb-10 max-w-2xl mx-auto">
              The fear fades. The uncertainty transforms into clarity. The burnout gives way to sustainable high performance.
            </p>
            <a
              href="https://calendar.app.google/evsn9LsubajagFF3A"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center gap-3"
            >
              Schedule a Conversation
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
